import getQueryParams from '@/utils/getQueryParams'

import {
    BookingQuoteDTO,
    BookingQuotePrestation,
    BookingQuoteTraveller,
    BookingStatusEnum,
    QuoteResponseDTO,
    UserInfo,
} from '../../../../requests-module'
import { setRouteQuery } from '@/router/navigate'
import getBirthdateFromAge from '@/logic/getBirthdateFromAge'
import token from '@/state/token'
import bookingQuoteResponse from '@/state/bookingQuoteResponse'
import isHaveDetailResponse from '@/state/isHaveDetailResponse'

import { localeToEntity } from '@/config/localeToEntity'
import { CSETraveller } from '@/classes/CSE'
import { CSEService } from '@/service'
import getCookie from '@/utils/getCookie'

import tracking_booking from '@/tracking/tracking_booking'
import { Participant } from '@/classes/Participant'
import getCartTokenInLocalStorage from './getCartTokenInLocalStorage'
import updateCartTokenInLocalStorage from './updateCartTokenInLocalStorage'
import bookingQuoteResponse_edit from '@/state/bookingQuoteResponse_edit'
import { setBookingQuoteResponse } from '../setBookingQuoteResponse'
import loggedInUser from '@/state/loggedInUser'
import agentId from '@/state/agentId'

// Models

export const processPrestations = (
    prestationsStr: string
): BookingQuotePrestation[] => {
    const prestations: BookingQuotePrestation[] = []
    prestationsStr.split('--').forEach((prestationStr: string) => {
        const [id, quantity, travellers] = prestationStr.split('-')
        const prestation: BookingQuotePrestation = new BookingQuotePrestation(
            id,
            Number(quantity),
            Number(travellers)
        )
        prestations.push(prestation)
    })
    return prestations
}



const processTravellers = (
    participantsStr: string
): BookingQuoteTraveller[] => {
    const cseService: CSEService = new CSEService(window.locale)
    if (cseService.isCSE) cseService.init()

    const travellers: BookingQuoteTraveller[] = []
    const company = getCookie('company')

    const localStorageParticipants: Participant[] = JSON.parse(localStorage.getItem('participants') || '[]') as Participant[];

    participantsStr
        .split('-')
        .forEach((age: string, participantIndex: number) => {
            if (cseService.isCSE && (company === 'cgos' || company === 'cgosaura')) {
                const cseTraveler: CSETraveller =
                    cseService.extractCSETravellerByIndex(participantIndex)
                if (!cseTraveler) throw new Error('could not find cseTraveler')

                const birthdate = getBirthdateFromAge(
                    Number(cseTraveler.age)
                ) as string
                const participant: BookingQuoteTraveller =
                    new BookingQuoteTraveller(
                        Number(cseTraveler.age) >= 18 ? 'Adult' : 'Child',
                        birthdate,
                        cseTraveler.fisrtName,
                        cseTraveler.lastName
                    )
                travellers.push(participant)
            } else {
                const birthdate = getBirthdateFromAge(Number(age)) as string
                // TTL FOR THIS LOCALSTORAGE SET TO 365 DAYS
                const validDaysInLocalStorage = 365;
                let firstName: string | undefined,
                    lastName: string | undefined;
                if (localStorageParticipants) {
                    let stop = false;
                    for (let i = 0; i < localStorageParticipants.length && !stop; i++) {
                        const daysBetweenDates = (new Date().getTime() - new Date(localStorageParticipants[i].creationDate).getTime()) / (1000 * 60 * 60 * 24);
                        if (localStorageParticipants[i].age === Number(age) && daysBetweenDates <= validDaysInLocalStorage) {
                            firstName = localStorageParticipants[i].firstName;
                            lastName = localStorageParticipants[i].lastName;
                            localStorageParticipants.splice(i, 1);
                            stop = true;
                        }
                    }
                }
                const participant: BookingQuoteTraveller =
                    new BookingQuoteTraveller(
                        Number(age) >= 18 ? 'Adult' : 'Child',
                        birthdate,
                        firstName,
                        lastName
                    )
                travellers.push(participant)
            }
        })
    return travellers
}
const processEntity = (locale: string): string => {
    const entity = localeToEntity[locale.replaceAll('_', '-')]
    if (!entity) {
        throw new Error('failed to get entity for locale: ' + locale)
    }
    return entity
}

const bookingQuote = async (
    detail = true,
    force = false
): Promise<QuoteResponseDTO> => {
    // do not make unnecessary call
    if (!force) {
        if (!detail && bookingQuoteResponse.value) {
            return bookingQuoteResponse.value
        }
        if (isHaveDetailResponse.value) {
            if (!bookingQuoteResponse.value)
                throw new Error('bookingQuoteResponse has to be defined here')
            return bookingQuoteResponse.value
        }
    }

    const queryParams = getQueryParams()

    if (queryParams.agentId) {
        agentId.value = queryParams.agentId
    }

    const currentUrl = window.location.href;
    const cartTokenInLocalStorage = getCartTokenInLocalStorage(currentUrl)
    if (cartTokenInLocalStorage) setRouteQuery({ token: cartTokenInLocalStorage })

    const existingToken = token.value || queryParams.token || cartTokenInLocalStorage
    let response: QuoteResponseDTO | undefined

    if (existingToken) {
        // this checks availability for a state that exists, but it does not check whether there is any booking created in Iresa (i.e. panier en cours, quote or reservation)
        response = await window.tfServices.bookingService.getBookingTokenQuote(
            existingToken,
            detail
        )
        if (!response) throw new Error('quote call failed')
        token.value = existingToken

        // Set the userInfo if needed
        // TODO combine current user info with, info from endpoint
        if (response.userInfo && (!loggedInUser.value || !loggedInUser.value.email)) {
            const { userInfo } = response
            loggedInUser.value = buildUserInfo(userInfo)
        }

    } else {
        // try to create state from the data in the url
        // now depending on the presence of certain params, I do different calls
        const contract_id: string | undefined = queryParams.contract_id ? decodeURIComponent(queryParams.contract_id) : undefined
        const user_id: string | undefined = queryParams.user_id ? decodeURIComponent(queryParams.user_id) : undefined
        if (contract_id && user_id) {
            // this is for the case when the user came from customer account and has a Quote. The backend gets the quote from Iresa (getBooking), makes a getQuotedAvailability call with default (whatever is in the package, or just HS) selections, and combines whatever was selected in getBooking with the getQuotedAvailability response, thus creating state from whatever was made by the call center or in the old funnel (this is relevant while the AB test is running, later the old funnel will be killed, hopefully)
            response = await window.tfServices.bookingService.getBookingByContractAndUser(
                contract_id,
                user_id,
                detail
            )
            if (response?.boReference) setRouteQuery({ ...queryParams, token: response.boReference })
        } else {
            // this means there is no booking in Iresa to get, so we just create a state from getQuotedAvailability
            const dto: BookingQuoteDTO = new BookingQuoteDTO( // this is the payload created from the values in the url
                queryParams.lihe_id,
                processTravellers(queryParams.participants),
                queryParams.start_date,
                queryParams.end_date,
                processPrestations(queryParams.prestations),
                queryParams.season,
                queryParams.prest_id,
                processEntity(window.locale.replace('_', '-'))
            )
            response = await window.tfServices.bookingService.postBookingQuote(
                dto,
                detail
            )
        }
        if (!response) throw new Error('quote call failed')
        token.value = response.boReference
        //If there is a booking and the status is reservation,
        // don't change the route to token because it will be redirected to 'paid' page anyway
        if (
            !response.booking ||
            response.booking.status !== BookingStatusEnum.RESERVATION
        ) {
            updateCartTokenInLocalStorage(currentUrl, response.boReference);
            setRouteQuery({ token: response.boReference })
        }
    }

    setBookingQuoteResponse(response)

    isHaveDetailResponse.value = !!detail

    tracking_booking()

    return response
}

// Function to create the user info object
function buildUserInfo(userInfo: UserInfo): UserInfo {
    const loggedInUserValue: UserInfo | null = loggedInUser.value
    return new UserInfo(
        userInfo.email,
        loggedInUserValue?.first_name || userInfo.first_name,
        loggedInUserValue?.last_name || userInfo.last_name,

        loggedInUserValue?.gender || userInfo.gender,
        loggedInUserValue?.postal_code || userInfo.postal_code,
        loggedInUserValue?.city || userInfo.city,
        loggedInUserValue?.phone_number || userInfo.phone_number,
        loggedInUserValue?.phone_code || userInfo.phone_code,
        loggedInUserValue?.country || userInfo.country,
        loggedInUserValue?.address || userInfo.address,
        loggedInUserValue?.site_locale || userInfo.site_locale,
        loggedInUserValue?.iresa_id || userInfo.iresa_id
    );
}

export default bookingQuote
